// Conexão com o servidor de jogo (Colyseus). Guarda o token de reconexão para
// voltar à partida após queda de rede, troca de app no celular ou recarregar a página.
import { Client, type Room } from "colyseus.js";
import { api } from "./api";
import { config } from "./config";

const RECONNECT_KEY = "pokechess:reconnect";

export type Mode = "normal" | "ranked" | "custom" | "tutorial";

let client: Client | null = null;
function getClient(): Client {
  client ??= new Client(config.gameUrl);
  return client;
}

function remember(room: Room) {
  try {
    sessionStorage.setItem(RECONNECT_KEY, JSON.stringify({ token: room.reconnectionToken, at: Date.now() }));
  } catch {
    /* sem storage: reconexão só enquanto a aba viver */
  }
}

export function forgetRoom(): void {
  try {
    sessionStorage.removeItem(RECONNECT_KEY);
  } catch {
    /* ignora */
  }
}

export function savedRoom(): { token: string; at: number } | null {
  try {
    const raw = sessionStorage.getItem(RECONNECT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export async function joinMatch(mode: Mode, extra: Record<string, unknown> = {}): Promise<Room> {
  const { token } = await api.gameToken();
  // tutorial é sempre uma sala nova só da pessoa
  const room = mode === "tutorial" ? await getClient().create(mode, { token, ...extra }) : await getClient().joinOrCreate(mode, { token, ...extra });
  remember(room);
  return room;
}

export interface QueueProgress {
  waitedMs: number;
  window: number;
  queued: number;
}

const gameHttp = () => config.gameUrl.replace(/^ws/, "http");

/**
 * Fila por MMR (normal/ranqueada): entra, acompanha e, quando a sala fecha,
 * entra nela com o lugar reservado. `signal` cancela (sai da fila).
 */
export async function queueMatch(mode: "normal" | "ranked", onProgress: (p: QueueProgress) => void, signal: AbortSignal): Promise<Room | null> {
  const { token } = await api.gameToken();
  const res = await fetch(`${gameHttp()}/queue`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ token, mode }) });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? `fila: ${res.status}`);
  const { ticket } = (await res.json()) as { ticket: string };
  const leave = () => void fetch(`${gameHttp()}/queue/${ticket}`, { method: "DELETE", keepalive: true }).catch(() => undefined);
  signal.addEventListener("abort", leave, { once: true });
  while (!signal.aborted) {
    let st: { status: string; reservation?: unknown } & Partial<QueueProgress>;
    try {
      st = await (await fetch(`${gameHttp()}/queue/${ticket}`, { signal })).json();
    } catch {
      if (signal.aborted) break;
      await new Promise((r) => setTimeout(r, 1500));
      continue;
    }
    if (st.status === "matched" && st.reservation) {
      signal.removeEventListener("abort", leave);
      const room = await getClient().consumeSeatReservation(st.reservation as Parameters<Client["consumeSeatReservation"]>[0]);
      remember(room);
      return room;
    }
    if (st.status === "gone") throw new Error("fila: ticket expirou");
    onProgress({ waitedMs: st.waitedMs ?? 0, window: st.window ?? 0, queued: st.queued ?? 1 });
    await new Promise((r) => setTimeout(r, 1000));
  }
  return null;
}

export async function reconnect(): Promise<Room | null> {
  const saved = savedRoom();
  if (!saved || Date.now() - saved.at > 2 * 3600_000) return null;
  try {
    const room = await getClient().reconnect(saved.token);
    remember(room);
    return room;
  } catch {
    forgetRoom();
    return null;
  }
}

/**
 * Volta para a partida em andamento, do jeito que der:
 * 1. token de reconexão (queda curta, mesma sala);
 * 2. a sala atual da partida, informada pela API (recarregou a página depois de 1 min);
 * 3. sala "resume" a partir do checkpoint (o nó do jogo foi desligado ou caiu).
 * Tenta por até `maxMs` porque, durante uma atualização, os nós podem estar reiniciando.
 */
export async function rejoinMatch(knownMatchId?: string, maxMs = 120_000): Promise<Room | null> {
  const direct = await reconnect();
  if (direct) return direct;
  const until = Date.now() + maxMs;
  let delay = 1000;
  while (Date.now() < until) {
    let where: { matchId: string; roomId: string } | null = null;
    try {
      where = (await api.currentMatch()).match;
    } catch {
      /* API fora: tenta de novo */
    }
    const matchId = where?.matchId ?? knownMatchId;
    if (!matchId && where === null && !knownMatchId) return null; // não há partida
    try {
      const { token } = await api.gameToken();
      if (where?.roomId) {
        try {
          const room = await getClient().joinById(where.roomId, { token });
          remember(room);
          return room;
        } catch {
          /* sala não existe mais: retoma pelo checkpoint */
        }
      }
      if (matchId) {
        try {
          const room = await getClient().joinOrCreate("resume", { token, matchId });
          remember(room);
          return room;
        } catch (e) {
          const msg = String((e as Error).message ?? e);
          const live = /match-live:(\S+)/.exec(msg);
          if (live) {
            const room = await getClient().joinById(live[1]!, { token });
            remember(room);
            return room;
          }
          if (/match-not-found|partida em andamento/.test(msg)) return null; // acabou
        }
      }
    } catch {
      /* rede/servidor reiniciando */
    }
    await new Promise((r) => setTimeout(r, delay));
    delay = Math.min(delay * 1.5, 5000);
  }
  return null;
}

/** Partida atual (compartilhada entre as telas de fila e de jogo). */
let current: Room | null = null;
export function currentRoom(): Room | null {
  return current;
}
export function setCurrentRoom(room: Room | null): void {
  current = room;
}
